import {
  imageFileTypes,
  roboticsFileTypes,
  videoFileTypes,
} from "@/shared/domain/files/fileTypes";

export const computeExtensionsArray = (
  serverExtensions: string[] | undefined,
) => {
  const fileExtensions = new Set<string>();

  if (serverExtensions) {
    serverExtensions.forEach(function (value) {
      fileExtensions.add(value);
    });
  }

  // Add useful image defaults
  if (imageFileTypes) {
    imageFileTypes.forEach(function (value) {
      fileExtensions.add(value);
    });
  }

  // Add useful video defaults
  if (videoFileTypes) {
    videoFileTypes.forEach(function (value) {
      fileExtensions.add(value);
    });
  }

  // Add useful robotics defaults
  if (roboticsFileTypes) {
    roboticsFileTypes.forEach(function (value) {
      fileExtensions.add(value);
    });
  }

  return Array.from(fileExtensions.values()).sort();
};

/**
 * True if `destinationPath` is `ancestorPath` itself or lies inside it.
 *
 * Compares path segments (split on "/"), not raw string prefixes, so a
 * sibling whose name merely starts with the same characters (e.g. moving
 * `foo` next to `foobar`) is not mistaken for a descendant of `foo`.
 */
export const isPathWithinSubtree = (
  destinationPath: string,
  ancestorPath: string,
) => {
  return (
    destinationPath === ancestorPath ||
    destinationPath.startsWith(`${ancestorPath}/`)
  );
};

export type MoveSettlementSummary =
  | { outcome: "all-succeeded" }
  | { outcome: "all-failed"; error: unknown }
  | { outcome: "partial"; failedCount: number; total: number };

/**
 * Interprets the outcome of firing one move request per selected item
 * (`Promise.allSettled`), since a multi-item breadcrumb drop can partially
 * succeed (e.g. one item's destination name collides, the rest don't).
 */
export const summarizeMoveSettlement = (
  results: PromiseSettledResult<unknown>[],
): MoveSettlementSummary => {
  const failedCount = results.filter(
    (result) => result.status === "rejected",
  ).length;

  if (failedCount === 0) {
    return { outcome: "all-succeeded" };
  }

  if (failedCount === results.length) {
    const [firstFailure] = results as PromiseRejectedResult[];
    return { outcome: "all-failed", error: firstFailure.reason as unknown };
  }

  return { outcome: "partial", failedCount, total: results.length };
};
